import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { useEditorStore, useFloorplanStore } from '@repo/front-end-state';
import {
  layoutHasGeometry,
  planSaveFailureReason,
  readPlanSaveConflict,
  retiredAfterLaunch,
  unverifiedMintBlock,
  type IdentifiedNode,
} from '@repo/front-end-utils';
import {
  adoptStoredUnderlays,
  createTwinPlan,
  createUnderlayImages,
  externaliseUnderlayImages,
  fetchPlanUnderlayImage,
  getTwinPlanById,
  getTwinPlans,
  persistTwinLifecycleStage,
  updateTwinPlan,
  uploadPlanUnderlayImage,
} from '@repo/front-end-services';
import type { StoredUnderlay } from '@repo/front-end-services';
import type { FloorPlannerLayout } from '@repo/frontend-types';
import { TwinLifecycleStageValues } from '@repo/frontend-types';
import Config from '../../common/Config';
import { ORG_BID, ORG_NAME, PLAN_NAME } from './constants';
import { decideDraftChoice, firstEditBasis, withLaunchBasis } from './draftChoice';
import type { FirstMatchSource } from './draftChoice';
import {
  decisionsUnsaved,
  useSuggestionDecisionStore,
  withSuggestionDecisions,
} from './observerWorkspace/suggestionDecisions';
import {
  prunedReferences,
  referencesUnsaved,
  useObserverStampStore,
  withObserverReferences,
} from './observerStamps';
import { PlanSaveConflictError, planSaveConflictSentence } from './planSaveConflict';
import { activatedLayoutFor } from './putBackFloor';
import { restoreSessionRecordsFrom } from './sessionRecords';
import { useDraftChoice } from './useDraftChoice';
import type { HydratePlan, ResumedPlan } from './useDraftChoice';
import { useDraftSaver } from './useDraftSaver';

/**
 * Loading and saving a twin's plan in the wizard: one module, so every read and write of the plan is
 * in one place (`onboarding-multiplayer-plan-sync-module`). `OnboardingPage` calls no plan service.
 *
 * Three hooks rather than one, each called where its code has always stood on the page, so their
 * effects keep their order among the page's own. `planSync.boundary.test.ts` pins that order.
 * Each returned function keeps its `useCallback`, so depend on its members, never on a hook's result
 * object, which is new each render.
 *
 * Go Live's three operations are not `async`, and hand back `persistPlan`'s own promise for the page
 * to await. A promise of their own would resume Go Live a tick later, behind a save queued after it,
 * which could then stamp the plan Go Live reads next. The boundary test pins this.
 */

/** Onboarding works with exactly one plan per twin: the newest, which is the highest id. */
const newestPlanOf = <T extends { id: number }>(
  summaries: readonly T[] | undefined,
): T | undefined => summaries?.slice().sort((a, b) => b.id - a.id)[0];

/**
 * What a plan save reports back to its caller.
 *
 * `reason` is the sentence explaining the refusal: the server's own where it named a cause an
 * operator can act on (`planSaveFailureReason`), or a client-side refusal's own authored sentence
 * (the unverified-identity block, the plan no longer being loaded) — and `null` where nothing
 * explained it: a dropped connection, a timeout, a bare status line. A caller that reports the
 * failure on its own surface composes the two with `describeFailedSave`, so the reason REPLACES
 * its retry advice rather than sitting beside it.
 */
export type PlanSaveOutcome = { saved: true } | { saved: false; reason: string | null };

/**
 * `persistPlan`'s options.
 *
 * A failed save opens no dialog: the caller reports the outcome where the operator is looking — the
 * draft tag, Go Live's banner, the missing-entity import, the fetched-plan adoption. One message, not
 * the same sentence in a dialog and then again under the button (`camera-observer-setup-save-refusal`,
 * Rob 2026-09-12). The *Save failed* dialog went with the last Save button (`floor-plan-page-draft`).
 *
 * `updateOnly`: refuse rather than CREATE a plan when the server has none. `unchangedSince`: refuse
 * unless the store still holds exactly this layout — for a save the operator did not ask for, which
 * must never carry their edits. Both are checked when the save actually runs, which may be after a
 * queued save settles: by then a plan can have been cleared, or an edit made. The layout is
 * compared rather than the dirty flag because the save ahead in the queue marks the store clean
 * when it lands, which hides an edit made while this one waited.
 */
type PersistPlanOptions = {
  updateOnly?: boolean;
  unchangedSince?: FloorPlannerLayout;
  /**
   * Whether this save carries the person's own work (`twin-launch-history-draft-choice`, R1.2) —
   * the default. A save that does stamps the plan's `draftBasis` as holding unlaunched work, on the
   * first edit. Go live's own saves, and the re-save of re-minted composite ids after a re-match,
   * pass `false`: they write what a launch or a load produced, which is not unlaunched work.
   */
  edit?: boolean;
  /**
   * The restore's own flush (`twin-launch-history-draft-choice`). While a draft swap runs, or after
   * one that could not be confirmed, every other save is refused — it would write the version on
   * screen over the one the swap put on the server. The flush runs BEFORE the swap is sent, and is
   * the one save that must still go through.
   */
  duringSwapFlush?: boolean;
};

/** `unchangedSince`'s refusal — not a failure: the operator's own Save will carry what was waiting. */
const SKIPPED_LAYOUT_CHANGED =
  'The plan changed while this save waited, so its own Save will carry it.';

type UsePlanLoadArgs = {
  accessToken: string | undefined;
  twinId: number | undefined;
  twinBid: string | undefined;
  sessionId: string;
  /** Truthy on an address whose two twin identifiers disagree. Its identity re-runs the open. */
  twinIdentityConflict: unknown;
  userEmail: string | null;
  modifiedByRef: MutableRefObject<string | undefined>;
  onSwapped: () => void;
};

/** The open: what the twin's saved plan is, and the plan id, state and stamp every save works from. */
export const usePlanLoad = ({
  accessToken,
  twinId,
  twinBid,
  sessionId,
  twinIdentityConflict,
  userEmail,
  modifiedByRef,
  onSwapped,
}: UsePlanLoadArgs) => {
  // ---- Plan persistence (single plan per twin) ----
  // The twin's saved plan id; null until one is created (or resumed). Held in
  // a ref (with state mirroring it for renders) so persistPlan always sees
  // the current id — a stale null here would create a duplicate plan.
  const planIdRef = useRef<number | null>(null);
  const [planId, setPlanId] = useState<number | null>(null);
  const assignPlanId = useCallback((id: number | null) => {
    planIdRef.current = id;
    setPlanId(id);
  }, []);
  /*
   * What the resume check found — not just whether it has run (`adoption-start-again`).
   *
   * `planLoading` was one boolean, which made three different outcomes one: the twin has NO saved
   * plan, the plans read FAILED, and the plan's own read failed. The Equipment step's delete
   * refusal needs them apart — "no saved plan" is the one state in which nothing can be placed, and
   * it is exactly where a twin is straight after an adoption, so treating it as "not loaded" refused
   * every Remove on the register for as long as the page was open. The two failures must stay
   * refusals. `unread` is a twin with no BID yet: nothing was asked, so nothing is known.
   */
  const [planState, setPlanState] = useState<'loading' | 'none' | 'loaded' | 'failed' | 'unread'>(
    'loading',
  );
  /**
   * When the resumed plan was last saved (`twin_plan.modified_at`) — the
   * *Draft saved* tag's time until this session saves (`observers-page-draft`, FR4). `null` when no
   * plan was resumed, or the store already held this session's plan.
   */
  const [planSavedAt, setPlanSavedAt] = useState<Date | null>(null);
  /**
   * Who saved the plan as the wizard last read or wrote it, for the history panel's current-draft
   * row (`twin-launch-history-panel`): `modified_by` from the resume read; the signed-in person
   * after a swap (the swap stamps them) or a save this session.
   */
  const [planSavedBy, setPlanSavedBy] = useState<string | null>(null);
  // True until the resume check has run — the plan sections wait on it so the
  // Floor Plan chooser can't flash (or be acted on) before a saved plan loads.
  const planLoading = planState === 'loading';

  /**
   * Which observers the live configuration's FIRST match keeps (`twin-launch-history-draft-choice`,
   * R2.4) — decided here, before a resumed plan is hydrated, never inferred by the hook. `null`
   * HOLDS the first match until the resume has decided; a twin with no saved plan (or none that
   * could be read) gets `running`, because a plan made this session (*Start from this twin*, an
   * import) holds no unlaunched work to protect.
   *
   * A ref read by the hook when its match runs, never React state: `hydratePlan` sets it and then
   * loads the store in one synchronous step, and a store update can render before a state update
   * lands — so state could hand the first match the PREVIOUS decision for exactly that render.
   */
  const firstMatchSourceRef = useRef<FirstMatchSource | null>(null);
  const getFirstMatchSource = useCallback(() => firstMatchSourceRef.current, []);
  /**
   * `twin_plan.modified_at` EXACTLY as the server last answered it — the stamp the two draft swaps
   * compare (`is distinct from` on a timestamptz), so a string, never a re-serialised `Date`, which
   * would drop the microseconds.
   */
  const planModifiedAtRef = useRef<string | null>(null);
  /** The live configuration's `restartMatch`, which is created after the swaps that call it. */
  const restartMatchRef = useRef<() => void>(() => undefined);

  /**
   * Put a plan the open (or a swap) settled on into the wizard: the store, a fresh
   * undo history and session baseline, the plan id and its stamp. One function for the resume and
   * both swaps, so they cannot hydrate differently.
   */
  const hydratePlan = useCallback(
    (plan: Parameters<HydratePlan>[0], source: FirstMatchSource) => {
      // Before the store: the first match reads this when the store's change reaches it.
      firstMatchSourceRef.current = source;
      /*
       * The drawn-against records are read into the stamp store (`restoreSessionRecordsFrom`, below)
       * and kept OUT of the plan store, so the session holds one copy of them, not two. A copy left
       * in the store would go stale with the first confirm, and the launch claim and record — which
       * file the store's layout — would carry it into launch history
       * (`onboarding-polish-observer-notices`, review).
       */
      useFloorplanStore.getState().loadLayout(
        // Keep mine decisions live in their own store too (`restoreSessionRecordsFrom`), one copy.
        withSuggestionDecisions(withObserverReferences(plan.layout, {}), []),
        sessionId,
        ORG_BID,
        plan.name || PLAN_NAME,
        ORG_NAME,
      );
      const editor = useEditorStore.getState();
      editor.clearHistory();
      editor.clearModified();
      // The session's drawn-against records and decisions, from the plan it opened on. Beside
      // `clearModified`, never inside it.
      restoreSessionRecordsFrom(plan.layout);
      assignPlanId(plan.id);
      planModifiedAtRef.current = plan.modifiedAt;
      // `modified_at` only, never `created_at`: a creation time would claim a save that is not the
      // last one. Absent, the tag says *Draft saved* with no time until this session saves (FR4).
      const savedAt = plan.modifiedAt ? new Date(plan.modifiedAt) : null;
      setPlanSavedAt(savedAt && !Number.isNaN(savedAt.getTime()) ? savedAt : null);
      // The resume read carries `modifiedBy`; a swap's plan does not, and the swap stamped this person.
      setPlanSavedBy(
        plan.modifiedBy !== undefined ? plan.modifiedBy : (modifiedByRef.current ?? null),
      );
      setPlanState('loaded');
    },
    [sessionId, assignPlanId, modifiedByRef],
  );

  /** Production, the dropped-draft slot and the swaps (`twin-launch-history-draft-choice`). */
  const draftChoice = useDraftChoice({
    middlewareApi: Config.middlewareApi,
    accessToken,
    twinId,
    hydrate: hydratePlan,
    restartMatch: () => restartMatchRef.current(),
    userEmail,
    onSwapped,
  });
  const draftChoiceRef = useRef(draftChoice);
  draftChoiceRef.current = draftChoice;

  /*
   * A different twin, in place: another twin's question, slot, notice and first-match decision say
   * nothing about this one. ⚠ Declared BEFORE the plan-load effect below, so it runs first in the
   * same commit: the load starts this twin's production read, and a reset after it would bump the
   * generation under that read and discard its launch id as stale (PR review, 2026-10-06).
   */
  const { reset: resetDraftChoice } = draftChoice;
  useEffect(() => {
    resetDraftChoice();
    firstMatchSourceRef.current = null;
  }, [twinBid, sessionId, resetDraftChoice]);

  // Resume: if the twin already has a saved plan (an earlier onboarding
  // session that never launched), load it so work carries on where it left
  // off. Onboarding works with exactly one plan per twin — the newest is
  // resumed if several exist.
  //
  // Resume does NOT seed from the live model, and deliberately still does not: it restores a plan
  // the user was working on, and a twin with a live model but no saved plan legitimately has
  // nothing to resume.
  //
  // Starting from the live model is now available, but as an explicit, user-chosen action rather
  // than an automatic seed — the "Start from this twin" card in the Floor Plan chooser
  // (`OnboardingPlanner` + `useLiveTwinPlan`, spec `plan-from-existing-twin`, epic ledger row 27).
  // Which is why this effect and that option cannot both fire: the option is only offered when
  // `planId` is null, i.e. when resume found nothing.
  //
  // ⚠ The comment that used to sit here said seeding belonged to the twin-editing epic because
  // `convertPlanToTwin` "cannot reproduce the model a seeded plan came from". Both halves have
  // expired: the cyclic-area defect behind that was fixed on 2026-07-31 (all four measured staging
  // twins now round-trip losslessly), and Rob pulled the capability back into this epic on
  // 2026-08-04. The round trip is pinned by `PlannerConverter.assetIdentity.test.ts`.
  useEffect(() => {
    /*
     * Re-enter "unknown" on every run, the `setBidTwin(null)` discipline. `twinBid` is a search
     * param, so a twin change re-renders without remounting: without this, the previous twin's
     * `none` stood while the new twin's plans were read, and `planAbsent` let Remove through on
     * rows that twin's saved plan places (review, 2026-09-24 — four reviewers independently).
     * Every branch below settles it again; a token refresh on the same twin settles it in the
     * same tick, before any await.
     */
    setPlanState('loading');
    setPlanSavedAt(null);
    // Same refusal as the page's register read: do not pull the `?twinBid=` twin's saved plan into
    // the floorplan store on an address whose two twin identifiers disagree.
    if (twinIdentityConflict) {
      // Deliberately not read, so not known — a refusal, like a failed read.
      setPlanState('failed');
      return;
    }
    if (!accessToken || !twinBid) {
      if (accessToken) setPlanState('unread');
      return;
    }
    /** No saved plan to decide about: a plan made this session has no unlaunched work. */
    const nothingToDecide = () => {
      if (useFloorplanStore.getState().twinBID !== sessionId)
        firstMatchSourceRef.current = 'running';
    };
    let cancelled = false;

    (async () => {
      // Never clobber work already in the stores for this session. This is also what makes the
      // open decide once per session (R2.3): it decides only on the way to hydrating.
      if (useFloorplanStore.getState().twinBID === sessionId) {
        setPlanState('loaded');
        return;
      }

      // Production, for the open's decision — read beside the plan, never after it (R6.1).
      const productionRead = draftChoiceRef.current.readProduction();
      const summaries = await getTwinPlans(Config.middlewareApi, accessToken, twinBid);
      if (cancelled) return;

      // `getTwinPlans` answers `undefined` on a failed read and `[]` for a twin with no plans —
      // the one distinction the delete refusal turns on, so it is kept rather than folded by `?.`.
      if (summaries === undefined) {
        nothingToDecide();
        setPlanState('failed');
        return;
      }
      const latest = newestPlanOf(summaries);
      if (!latest) {
        nothingToDecide();
        setPlanState('none');
        return;
      }
      // The dropped-draft read, beside the plan read, for Versions; it never holds the open (R6.1).
      void draftChoiceRef.current.refreshDropped(latest.id);
      const plan = await getTwinPlanById(Config.middlewareApi, accessToken, latest.id);
      if (cancelled) return;
      if (!plan?.layout) {
        // A plan EXISTS and could not be read: unknown, never "no plan" — it may place every row.
        // The id only; never the error object (the bearer-token rule in `getTwinPlans.ts`).
        console.error('Could not read the twin plan', latest.id);
        nothingToDecide();
        setPlanState('failed');
        return;
      }
      const resumed: ResumedPlan = {
        id: plan.id,
        name: plan.name || PLAN_NAME,
        layout: plan.layout as unknown as FloorPlannerLayout,
        modifiedAt: (plan.modified_at as string | null | undefined) ?? null,
        modifiedBy: (plan.modified_by as string | null | undefined) ?? null,
      };
      /*
       * What loads (`twin-launch-history-draft-choice`, R2): decided BEFORE the plan is hydrated, so
       * the live configuration's first match waits for it. The plan always loads as saved, with the
       * first-match source and notice the rules give; nothing is written on open. The opening
       * *keep the draft / use production* question was removed (Rob, 2026-10-27,
       * `remove-draft-choice-modal`): Versions' *Discard Draft* is the way back to production.
       */
      const production = await productionRead;
      if (cancelled) return;
      const decision = decideDraftChoice({
        layout: resumed.layout,
        planModifiedAt: resumed.modifiedAt,
        production,
      });
      draftChoiceRef.current.setNotice(decision.notice);
      hydratePlan(resumed, decision.firstMatchSource);
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, twinBid, sessionId, assignPlanId, twinIdentityConflict, hydratePlan]);

  return {
    planId,
    planIdRef,
    assignPlanId,
    planState,
    planLoading,
    planSavedAt,
    planSavedBy,
    planModifiedAtRef,
    firstMatchSourceRef,
    getFirstMatchSource,
    restartMatchRef,
    draftChoice,
    draftChoiceRef,
  };
};

type UsePlanSaveArgs = {
  load: ReturnType<typeof usePlanLoad>;
  accessToken: string | undefined;
  twinBid: string | undefined;
  sessionId: string;
  draftSaveEnabled: boolean;
  duplicateCodeHint: string | undefined;
  goLiveInFlightRef: MutableRefObject<boolean>;
  observerEditPendingRef: MutableRefObject<boolean>;
  modifiedByRef: MutableRefObject<string | undefined>;
  getAccessTokenSilently: () => Promise<string>;
};

/** The models a launch compares: what was live, and what is going live. */
type LaunchModels = { live: IdentifiedNode | null; tree: IdentifiedNode };

/** Every write of the plan: the save, the draft saver, the flush before a swap, Go Live's saves. */
export const usePlanSave = ({
  load,
  accessToken,
  twinBid,
  sessionId,
  draftSaveEnabled,
  duplicateCodeHint,
  goLiveInFlightRef,
  observerEditPendingRef,
  modifiedByRef,
  getAccessTokenSilently,
}: UsePlanSaveArgs) => {
  const { planIdRef, assignPlanId, planModifiedAtRef, firstMatchSourceRef, draftChoiceRef } = load;
  // In-flight save, held as its promise rather than a boolean. persistPlan fires from the draft
  // saver, Go Live and several background saves, and two calls in one tick would race (worst case
  // double-creating the twin's single plan).
  //
  // It holds the PROMISE, not a flag, because Go Live now depends on the answer: the old
  // `if (savingRef.current) return;` resolved immediately with nothing, so a caller that needed
  // the plan to be on the server could sail past a save that had not finished — and activate from
  // the in-memory layout while the persisted plan was stale or absent. A second caller now waits
  // for the first save and gets its real result.
  const savePromiseRef = useRef<Promise<PlanSaveOutcome> | null>(null);
  /** The twin whose *3D scene* progress write has settled this session (FR11, in `runPersistPlan`). */
  const lifecycleThreeDSceneDoneRef = useRef<string | null>(null);
  /** The twin whose *3D scene* progress write is running now, so saves meanwhile start no other. */
  const lifecycleThreeDSceneInFlightRef = useRef<string | null>(null);
  /** The draft saver's `noteSaved`, which is mounted after the save path it hears from. */
  const draftNoteSavedRef = useRef<() => void>(() => undefined);
  /**
   * The draft saver's `request`, for the subscriptions declared after the saver (observer stamps,
   * suggestion decisions). The generated-totals effect no longer asks for a save
   * (`plan-save-version-check`, R9).
   */
  const draftRequestRef = useRef<() => void>(() => undefined);
  /** The saver's `cancel`: a refused save drops what queued behind it, so the tag can say why. */
  const draftCancelRef = useRef<() => void>(() => undefined);

  /** The twin this page is on now — a save that lands for another one must not speak for it. */
  const currentTwinBidRef = useRef(twinBid);
  currentTwinBidRef.current = twinBid;

  /**
   * After a create whose outcome is unknown, learn whether the twin now has a plan and hold its
   * id, so the next save updates it rather than creating a second one. Never throws.
   */
  const adoptExistingPlanId = useCallback(async (): Promise<void> => {
    if (!accessToken || !twinBid) return;
    try {
      const summaries = await getTwinPlans(Config.middlewareApi, accessToken, twinBid);
      const latest = newestPlanOf(summaries);
      if (latest) assignPlanId(latest.id);
    } catch {
      // Best effort: the failed save is reported regardless.
    }
  }, [accessToken, twinBid, assignPlanId]);

  // Persist the plan via the twin-plans API: first save creates the twin's
  // single plan, later saves update it. The Floor Plan and Camera Feeds
  // sections both edit the layout (cameras live inside it). Assets persist on
  // upload; observers aren't persistable yet. Reads the layout straight from
  // the store (not the render closure) so callers can persist immediately
  // after a store write — e.g. right after a camera feed is linked.
  /*
   * Stored floor-plan PDF images (`complex-measures-in-configuration-underlay-storage`). Owned HERE,
   * not by the planner, because this page's save is what uploads an inline image — and it seeds the
   * uploaded bytes into this same cache so the planner draws them without fetching them back. The
   * token is asked for per load (Auth0 caches it), so a long session never reads with an expired
   * one. No `?twinBid=` means no way to read: the load throws and the canvas says "could not load".
   */
  const underlayImages = useMemo(
    () =>
      createUnderlayImages(
        async (imageRef, signal) => {
          if (!twinBid) throw new Error('No twin BID, so stored floor plan images cannot be read.');
          return fetchPlanUnderlayImage(
            Config.middlewareApi,
            await getAccessTokenSilently(),
            twinBid,
            imageRef,
            signal,
          );
        },
        /*
         * An imported PDF is stored at once (`complex-measures-in-configuration-underlay-layers`),
         * under the wizard's real twin BID. No `?twinBid=` means nowhere to file it: answered as the
         * route answers an unknown twin (*This twin could not be found…*), never filed under a guess.
         */
        async (blob, signal) =>
          twinBid
            ? uploadPlanUnderlayImage(
                Config.middlewareApi,
                await getAccessTokenSilently(),
                twinBid,
                blob,
                signal,
              )
            : { data: null, error: { message: 'No twin BID', status: 404 } },
      ),
    [twinBid, getAccessTokenSilently],
  );
  useEffect(() => () => underlayImages.dispose(), [underlayImages]);

  /*
   * The layout a post-save image swap replaced, mapped to the one that replaced it. Several checks on
   * this page ask "has the plan changed since X?" by IDENTITY (`unchangedSince`, the re-match save,
   * the removal save's rollback). The swap gives the store a new layout object without changing the
   * plan's meaning, so those checks resolve X through this map first — otherwise a swap would read
   * as an operator's edit: a queued save skipped, or a clean plan marked dirty.
   */
  const swappedLayoutsRef = useRef(new WeakMap<FloorPlannerLayout, FloorPlannerLayout>());
  const currentLayoutOf = useCallback((layout: FloorPlannerLayout): FloorPlannerLayout => {
    let current = layout;
    for (
      let next = swappedLayoutsRef.current.get(current);
      next;
      next = swappedLayoutsRef.current.get(current)
    ) {
      current = next;
    }
    return current;
  }, []);

  /**
   * Stamp the plan as holding unlaunched work, on the FIRST edit (`twin-launch-history-draft-choice`,
   * R1.2) — when, and from which launch. A no-op once stamped. The swap is recorded like any other
   * replacement of the store's layout, so a caller holding the pre-stamp layout still finds the
   * current one (`currentLayoutOf`).
   */
  const stampFirstEdit = useCallback(() => {
    const store = useFloorplanStore.getState();
    if (!store.layout || store.twinBID !== sessionId) return;
    const basis = firstEditBasis(
      store.layout,
      draftChoiceRef.current.productionLaunchUuidRef.current,
      new Date().toISOString(),
      // Who started the draft (`twin-details-page-version-changes`, R5) — the same person the save
      // names as `modified_by`.
      modifiedByRef.current ?? null,
    );
    if (!basis) return;
    const before = store.layout;
    store.setDraftBasis(basis);
    const after = useFloorplanStore.getState().layout;
    if (after && after !== before) swappedLayoutsRef.current.set(before, after);
  }, [sessionId, draftChoiceRef, modifiedByRef]);

  const runPersistPlan = useCallback(
    async (
      updateOnly = false,
      unchangedSince?: FloorPlannerLayout,
      edit = true,
    ): Promise<PlanSaveOutcome> => {
      // `let`: a first edit's stamp (below) replaces the layout this save sends.
      let { layout: liveLayout } = useFloorplanStore.getState();
      const { twinBID: liveTwinBID } = useFloorplanStore.getState();
      if (!liveLayout || liveTwinBID !== sessionId || !accessToken || !twinBid) {
        // Nothing to save — the store holds no layout for this session, or there is no token.
        // Retrying cannot fix it, so it carries its own sentence rather than `null`, and it is
        // logged: a queued Save that lands after a rollback would otherwise produce nothing at
        // all (`/review-code`, 2026-09-12). No dialog: a modal for "there is nothing to save"
        // would be worse than the reason in the draft tag.
        console.error('Failed to save plan: no layout is loaded for this session, or no token.');
        return {
          saved: false,
          reason: 'The plan is no longer loaded for this session. Reload the page and try again.',
        };
      }

      // A save that may only UPDATE finds no plan on the server: nothing to correct, and a POST from
      // a caller that never asked to create one would be a plan the operator did not save.
      if (updateOnly && planIdRef.current == null) {
        return { saved: false, reason: 'There is no saved plan to update.' };
      }
      if (unchangedSince !== undefined && liveLayout !== currentLayoutOf(unchangedSince)) {
        return { saved: false, reason: SKIPPED_LAYOUT_CHANGED };
      }

      /*
       * ⚠ **Refuse the SAVE while a new placement's identity is unverified** (`plan-entity-identity`,
       * FR4 / AC8).
       *
       * The placement itself was never blocked — a failed live-model read must not make a dead planner
       * out of a transient network fault — but a machine minted without that model may carry a BID the
       * model already holds, and **nothing tombstones**, so the plan cannot answer the question for
       * itself. Saving anyway is what would let two entities collapse into one at the next activation.
       *
       * Read from the store rather than a render closure, exactly as the layout above is, and for the
       * same reason: `persistPlan` fires from several places including the moment a camera feed is
       * linked, so a closure value can be a render stale.
       *
       * ⚠ **Before the request, and it reports `saved: false`.** A `saved: true` would tell Go Live
       * the plan is on the server when it is not — which is precisely how a persisted plan and a live
       * model diverge silently.
       */
      // `twinBid` is the wizard's `?twinBid=` — the twin's real dGraph BID, and the fold's prefix.
      // NOT the floorplan store's `twinBID`, which is `onboarding-<sessionId>` here.
      const identityBlock = unverifiedMintBlock(useFloorplanStore.getState(), twinBid);
      if (identityBlock) {
        // A client-side refusal with its own authored sentence, which travels as the reason — the
        // same one-message rule as a server refusal.
        return { saved: false, reason: identityBlock.message };
      }

      // Every step's work counts as unlaunched work — observers, complex measures, the floor plan,
      // the camera links — so the stamp is applied here, on the save, rather than in each step.
      // Dirty only: a Save pressed on an unchanged plan is not an edit.
      if (edit && useFloorplanStore.getState().isDirty) {
        stampFirstEdit();
        liveLayout = useFloorplanStore.getState().layout ?? liveLayout;
      } else if (
        !liveLayout.draftBasis &&
        !liveLayout.observerDraft &&
        firstMatchSourceRef.current === 'running' &&
        (referencesUnsaved(liveLayout) || decisionsUnsaved())
      ) {
        /*
         * A QUIET save — drawn-against records only — on a plan with no unlaunched-work marker at all
         * (one last saved before the marker existed, and opened as the running twin). Such a plan is
         * judged by `modified_at` against the latest launch, and this save moves `modified_at`, so the
         * next open would take a confirm for unlaunched work and open it as a draft, not as the
         * running twin — which Rob ruled a confirm never does (2026-10-06). So it is marked first
         * as what it is: launched work, based on production's latest launch. Only when the open
         * took the running twin (`'running'`): an unmarked plan opened as a draft IS unlaunched
         * work, and stays so.
         */
        const store = useFloorplanStore.getState();
        const before = store.layout;
        store.setDraftBasis({
          editedAt: null,
          basedOnLaunch: draftChoiceRef.current.productionLaunchUuidRef.current,
        });
        const after = useFloorplanStore.getState().layout;
        if (before && after && after !== before) swappedLayoutsRef.current.set(before, after);
        liveLayout = after ?? liveLayout;
      }

      /*
       * The drawn-against records go with every save (`onboarding-polish-observer-notices`, R12–R13):
       * the session's, for observers still on the plan, or none — so what the server holds always
       * matches the session, and a record loaded with the plan for an observer that has since gone
       * is dropped. A separate object: `liveLayout` stays the store's, for the clean check below.
       */
      const sentReferences = prunedReferences(liveLayout, useObserverStampStore.getState().stamps);
      // Keep mine decisions ride every save the same way (`observer-autogen-review-walkthrough`, R5.3).
      const sentDecisions = useSuggestionDecisionStore.getState().decisions;
      const sentLayout = withSuggestionDecisions(
        withObserverReferences(liveLayout, sentReferences),
        sentDecisions,
      );

      let outcome: PlanSaveOutcome = { saved: false, reason: null };
      let storedUnderlays: StoredUnderlay[] = [];
      try {
        const name = liveLayout.name || PLAN_NAME;
        /*
         * No floor-plan image travels inside the plan (`complex-measures-in-configuration-underlay-
         * storage`): any background still carrying its image inline is stored first, and the plan
         * is sent with references only. All or nothing — if an image cannot be stored, the plan is
         * not sent, and the authored sentence goes down the same failure path as a refused save.
         */
        const externalised = await externaliseUnderlayImages(
          Config.middlewareApi,
          accessToken,
          twinBid,
          sentLayout,
        );
        if (!externalised.ok) throw new Error(externalised.message);
        const layoutPayload = externalised.layout as unknown as Record<string, unknown>;

        if (planIdRef.current != null) {
          const { data, error } = await updateTwinPlan(
            Config.middlewareApi,
            accessToken,
            planIdRef.current,
            {
              name,
              layout: layoutPayload,
              modified_by: modifiedByRef.current,
              // R1: the version this tab last had from the server. The server writes only from it.
              expected_modified_at: planModifiedAtRef.current,
            },
          );
          if (error) {
            // R7: a refusal because the copy is stale, or someone else is launching, is not a
            // failure to retry. It carries its own sentence and blocks saving until a reload.
            const conflict = readPlanSaveConflict(error);
            if (conflict) {
              throw new PlanSaveConflictError(
                planSaveConflictSentence(conflict, modifiedByRef.current),
                conflict,
              );
            }
            throw new Error(error.message ?? 'Update failed');
          }
          // The stamp exactly as the server answered it — what the draft swaps compare against.
          const stamped = (data as { modified_at?: string | null } | null)?.modified_at;
          if (stamped !== undefined) planModifiedAtRef.current = stamped;
        } else {
          const { data, error } = await createTwinPlan(Config.middlewareApi, accessToken, {
            twin_bid: twinBid,
            name,
            layout: layoutPayload,
          });
          if (error) {
            /*
             * The create is a POST and `twin_plan.twin_id` carries no unique constraint, so a
             * failure whose outcome is unknown — the 30 s timeout, a dropped connection — may
             * have left the row in place with nothing here holding its id, and the next save
             * would then POST a second plan for the twin. Re-read the twin's plans and adopt
             * the newest, exactly as the mount-time lookup does, so the retry UPDATES instead.
             * A failed re-read changes nothing: the save is still reported as failed below.
             * (Copilot review on #1623, on the timeout the review swept in.)
             */
            await adoptExistingPlanId();
            throw new Error(error.message ?? 'Create failed');
          }
          const created = data as { id?: number; modified_at?: string | null } | null;
          const createdId = created?.id;
          if (createdId != null) assignPlanId(createdId);
          planModifiedAtRef.current = created?.modified_at ?? null;
        }

        // Clean only if the store still holds what was sent. With images to upload first, a save
        // can take many seconds, and an edit made meanwhile is not in it — marking it clean would
        // let the operator leave without being asked.
        if (useFloorplanStore.getState().layout === liveLayout && !observerEditPendingRef.current) {
          useFloorplanStore.getState().markClean();
        }
        storedUnderlays = externalised.stored;
        // Only for this twin: the stamp store is one store, and a save that outlived a twin change
        // must not tell the next twin what its server holds.
        if (currentTwinBidRef.current === twinBid) {
          useObserverStampStore.getState().markReferencesSaved(sentReferences);
          useSuggestionDecisionStore.getState().markSaved(sentDecisions);
        }
        outcome = { saved: true };
        // Every save that lands is the draft saved — the editor's own Save and Go Live's included —
        // provided it is still this twin's.
        if (currentTwinBidRef.current === twinBid) draftNoteSavedRef.current();
      } catch (err) {
        console.error('Failed to save plan:', err);
        if (err instanceof PlanSaveConflictError) {
          // Stops every further plan save for the visit and opens *Reload to continue* (R7).
          draftChoiceRef.current.blockUntilReload(err.message);
          // An edit made while this save was in flight queued a follow-up. Saving is now suspended,
          // so the saver would re-arm that follow-up for ever and the tag would read *Saving…*
          // instead of the refusal. Cancel it; the failure the saver records for this save stays.
          draftCancelRef.current();
        }
        // The server's own sentence where it sent one — the identity guard's and the register-link
        // check's refusals name what is wrong and what to do about it, and the fixed string they
        // replaced advised a retry that a refusal can never satisfy. The caller renders it.
        const reason = planSaveFailureReason(err);
        outcome = { saved: false, reason };
      }

      // The plan is saved, so the twin has a 3D scene. Measured against the live store snapshot
      // this save actually persisted, not the render closure's `planDrawn`, which can be one
      // render stale — persistPlan also fires the moment a camera feed is linked, potentially in
      // the same tick as the geometry that made the plan non-empty. An empty plan must not advance.
      //
      // Outside the try on purpose: this runs only after a save that genuinely succeeded, and a
      // throw here must not be caught by the handler above and reported as "Save failed" on a save
      // that worked.
      //
      // Deliberately NOT suppressed while Go Live is activating, even though Go Live writes
      // `graphSaved` moments later. An earlier version skipped it to save a round trip, and that lost
      // a real property: if activation then fails, `handleGoLive` returns before its own write, so the
      // twin would sit at `initiated` despite having a saved, geometry-bearing plan. Letting both fire
      // is safe because `persistTwinLifecycleStage` serialises per twin and the rule never demotes —
      // `graphSaved` simply wins when it lands.
      // The images this save stored: swap the store's inline copies for their references, so the
      // next save sends no image and makes no upload, then draw them from the bytes in hand. After
      // the try on purpose — the plan has landed, and nothing here may report it as failed; a seed
      // that cannot be made only means the planner fetches the image back.
      // Only into the session this save was for: the operator may have moved to another twin while
      // the images uploaded, and that twin's plan must not take this one's references.
      const store = useFloorplanStore.getState();
      if (outcome.saved && storedUnderlays.length > 0 && store.twinBID === sessionId) {
        const before = store.layout;
        adoptStoredUnderlays(storedUnderlays, store.attachStoredUnderlays, underlayImages);
        const after = useFloorplanStore.getState().layout;
        if (before && after && before !== after) swappedLayoutsRef.current.set(before, after);
      }

      if (!outcome.saved || !layoutHasGeometry(liveLayout)) return outcome;
      /*
       * Once per twin per session (`observers-page-draft`, FR11). The draft saver saves about once
       * a second while someone draws, and each of these is a twin GET plus, when the stage moves, a
       * twin PATCH that also re-writes the twin's dGraph entity — for a label that, once *3D scene*,
       * stays there until Go Live writes the next one (`handleGoLive`'s `GraphSaved` write,
       * untouched).
       *
       * Recorded only once the write reports the stage settled, so a failed read or PATCH is tried
       * again on the next save. Recorded only for the twin it ran for.
       */
      // One attempt at a time per twin, too: the service queues rather than coalesces, so saves
      // landing while a slow attempt runs would each queue another twin read behind it.
      if (
        lifecycleThreeDSceneDoneRef.current !== twinBid &&
        lifecycleThreeDSceneInFlightRef.current !== twinBid
      ) {
        const forTwin = twinBid;
        lifecycleThreeDSceneInFlightRef.current = forTwin;
        void (async () => {
          try {
            const settled = await persistTwinLifecycleStage(
              Config.middlewareApi,
              accessToken,
              forTwin,
              TwinLifecycleStageValues.ThreeDScene,
            );
            if (settled) lifecycleThreeDSceneDoneRef.current = forTwin;
          } finally {
            if (lifecycleThreeDSceneInFlightRef.current === forTwin) {
              lifecycleThreeDSceneInFlightRef.current = null;
            }
          }
        })();
      }
      return outcome;
    },
    [
      accessToken,
      twinBid,
      sessionId,
      assignPlanId,
      adoptExistingPlanId,
      underlayImages,
      currentLayoutOf,
      stampFirstEdit,
      planIdRef,
      firstMatchSourceRef,
      planModifiedAtRef,
      modifiedByRef,
      draftChoiceRef,
      observerEditPendingRef,
    ],
  );

  /**
   * Save the plan and report whether it actually reached the server — and, when it did not, the
   * server's reason where there is one (`PlanSaveOutcome`).
   *
   * Every caller reports a failure itself — the draft saver in its tag, Go Live in its banner; no
   * dialog opens here. Go Live must read the result, because activating from a plan the server does
   * not have is how the persisted plan and the live model silently diverge.
   */
  const persistPlan = useCallback(
    async (options?: PersistPlanOptions): Promise<PlanSaveOutcome> => {
      // A save already running is WAITED FOR and then followed by this caller's own save — not
      // joined and reported as success.
      //
      // Joining looks equivalent and is not: `runPersistPlan` snapshots the store when it starts, so
      // the in-flight save may predate the write this caller is trying to persist. Returning its
      // result would tell Go Live "saved" about a payload that never contained the stamped entity
      // codes. Chaining costs one extra round trip in a rare window and makes `saved` mean what
      // its callers read it to mean: the layout as of this call is on the server.
      while (savePromiseRef.current) {
        // `catch`: a rejected in-flight save is the other caller's to report, not ours to inherit.
        await savePromiseRef.current.catch(() => undefined);
      }
      // A draft swap is running, or one could not be confirmed: refuse, rather than write the
      // version on screen over the server's (see `duringSwapFlush`).
      if (draftChoiceRef.current.isSavingBlocked() && !options?.duringSwapFlush) {
        return { saved: false, reason: draftChoiceRef.current.savingBlockedReason() };
      }

      const run = runPersistPlan(
        options?.updateOnly ?? false,
        options?.unchangedSince,
        options?.edit ?? true,
      );
      savePromiseRef.current = run;
      try {
        return await run;
      } finally {
        if (savePromiseRef.current === run) savePromiseRef.current = null;
      }
    },
    [runPersistPlan, draftChoiceRef],
  );

  /**
   * Mark the plan as launched work, based on `launchUuid`, and save it — Go live, once the launch
   * is live (`twin-launch-history-draft-choice`, R1.4). Never a stamp of unlaunched work. Resolves
   * whether the plan is now saved that way; nothing to do counts as saved.
   */
  const resetDraftMarker = useCallback(
    async (launchUuid: string | null | undefined): Promise<boolean> => {
      const layout = useFloorplanStore.getState().layout;
      if (!layout) return true;
      /*
       * The launch is now the reference every observer is compared against, so the drawn-against
       * records go (`onboarding-polish-observer-notices`, R15): cleared here, and dropped from the
       * plan by the save below — which therefore must run while the server still holds any.
       */
      useObserverStampStore.getState().clearStamps();
      // Already marked as exactly this launch: nothing to write. Anything else — unlaunched work, a
      // basis from an earlier launch, or NO marker at all — is written, so every launched plan
      // carries an explicit marker. An unmarked plan would otherwise fall back to `modified_at`,
      // and any save landing after the record (a re-minted-ids re-save) would make the plan just
      // launched read as unlaunched work.
      const basis = layout.draftBasis;
      if (
        basis &&
        basis.editedAt === null &&
        basis.basedOnLaunch === (launchUuid ?? null) &&
        !layout.observerDraft &&
        // A skipped-entities note goes with the launch (`floor-plan-page-start`, R12), so a plan
        // still carrying one is saved once more to drop it.
        !layout.adoptionSkipped &&
        // A renumbering record goes with the launch too (`floor-plan-page-launch-bid-changes`, R15).
        !layout.entityCodeChanges?.length &&
        !referencesUnsaved(layout) &&
        !decisionsUnsaved()
      ) {
        return true;
      }
      useFloorplanStore.getState().replaceLayout(withLaunchBasis(layout, launchUuid ?? null));
      return (await persistPlan({ edit: false })).saved;
    },
    [persistPlan],
  );

  /** Go Live's entity codes, stamped on: into the store and saved. The page reports a failure. */
  const saveStampedCodes = useCallback(
    (layout: FloorPlannerLayout): Promise<PlanSaveOutcome> => {
      useFloorplanStore.getState().replaceLayout(layout);
      return persistPlan({ edit: false });
    },
    [persistPlan],
  );

  /**
   * The codes this launch takes out of the live twin, retired before anything is destroyed, with the
   * save that makes them durable: `null` when no layout is loaded or none are retired. `retired` is
   * their count, for the page's log line.
   */
  const saveRetiredCodes = useCallback(
    ({ live, tree }: LaunchModels): { retired: number; save: Promise<PlanSaveOutcome> | null } => {
      const retiringLayout = useFloorplanStore.getState().layout;
      if (!retiringLayout) return { retired: 0, save: null };
      const retired = retiredAfterLaunch(live, tree, retiringLayout.retiredEntityCodes, twinBid);
      const before = retiringLayout.retiredEntityCodes ?? [];
      if (retired.length !== before.length) {
        useFloorplanStore
          .getState()
          .replaceLayout({ ...retiringLayout, retiredEntityCodes: retired }, { keepDirty: true });
        // An earlier undo step holds the plan WITHOUT these codes, and restoring it would let the
        // autosave drop them (#2115). The success path clears history anyway.
        useEditorStore.getState().clearHistory();
      }
      /*
       * DURABLE before the destructive call, and not only when the list grew: a retry finds the
       * codes already in memory from the press that failed, and memory is not proof they were
       * saved (#2115). A failed save stops here, with nothing changed.
       */
      return {
        retired: retired.length,
        save: retired.length > 0 ? persistPlan({ edit: false }) : null,
      };
    },
    [persistPlan, twinBid],
  );

  /**
   * Stamp the plan as the source of what is now live, and start its save. `null` when no layout is
   * loaded, so there is nothing to save.
   */
  const saveActivatedPlan = useCallback(
    ({ live, tree }: LaunchModels): Promise<PlanSaveOutcome> | null => {
      const activatedLayout = useFloorplanStore.getState().layout;
      // Go Live has already returned with no twin, so that half narrows the type and nothing more.
      if (!activatedLayout || !twinBid) return null;
      useFloorplanStore.getState().replaceLayout(
        activatedLayoutFor(activatedLayout, new Date().toISOString(), {
          // Every space and machine BID this launch takes out of the live twin is retired (R8).
          live,
          tree,
          twinBid,
        }),
      );
      // Every undo step from before this launch carries a plan the launch has superseded —
      // removed floors among them, which an undo would offer to put back after they have gone
      // from the live twin (`floor-plan-page-floors`, review).
      useEditorStore.getState().clearHistory();
      // Awaited by the page, unlike Go Live's lifecycle write, because this save carries this launch's
      // retired entity codes and the floor-id high-water mark, not just the stamp: losing them
      // lets a later mint reissue a retired BID. Still never fatal: the graph genuinely was
      // saved, so a failure is logged and the run continues.
      //
      // `'caller'`, and Go Live deliberately renders nothing: the activation SUCCEEDED, so
      // a *Save failed* dialog here — which is what the default used to open, a moment before
      // the success summary replaced it — reported a success as a failure with advice that
      // could not help. The page's log line is the report (`/review-code`, 2026-09-12).
      return persistPlan({ edit: false });
    },
    [persistPlan, twinBid],
  );

  /**
   * The draft saver (`observers-page-draft`, FR2/FR3/FR6/FR7). On a step in
   * `DRAFT_SAVE_SECTIONS`, an edit is in the plan store as soon as it is made and this persists it:
   * no Save button, and a failure is reported in the title bar's tag, never a dialog.
   *
   * - **Blocked** while two entities share a code: the server refuses such a plan, so a request
   *   would be noise. One save follows when the clash is cleared.
   * - **Suspended** while Go Live runs: its own pre-flight save carries the work, and `handleGoLive`
   *   cancels whatever was waiting.
   * - **Clean at fire time** means overtaken — by a load or another save — so nothing is sent.
   */
  const draft = useDraftSaver({
    save: () => persistPlan(),
    enabled: draftSaveEnabled,
    blocked: duplicateCodeHint ? { reason: duplicateCodeHint } : null,
    // Also while a draft swap runs, and after one that could not be confirmed: an autosave then
    // would write the version on screen over the one the swap put on the server.
    isSuspended: () => goLiveInFlightRef.current || draftChoiceRef.current.isSavingBlocked(),
    /*
     * Clean is the plan AND its drawn-against records. A record changed with nothing else — a
     * confirm on the Observers step — is still owed a save, and it goes as a quiet one: the plan is
     * not dirty, so `runPersistPlan` does not mark it as unlaunched work
     * (`onboarding-polish-observer-notices`, R12; Rob, 2026-10-06).
     */
    isClean: () =>
      !useFloorplanStore.getState().isDirty &&
      !referencesUnsaved(useFloorplanStore.getState().layout) &&
      !decisionsUnsaved(),
  });
  draftNoteSavedRef.current = draft.noteSaved;
  draftRequestRef.current = draft.request;
  draftCancelRef.current = draft.cancel;

  /*
   * A drawn-against record changed — a confirm, a draw, a redraw — so a save is owed even though the
   * plan itself is not dirty (`onboarding-polish-observer-notices`, R12). A subscription is safe here
   * where the draft saver's header warns against one, because a session start or a twin change
   * RESTORES the records and marks them saved in the same step — `saved` moves with them — and that
   * is skipped, so a load asks for nothing (and the tag never flickers to *Saving…* for it). Every
   * other change asks, and the saver's fire-time `isClean` decides; comparing against `saved` here
   * would read a value a save in flight is about to replace.
   */
  useEffect(
    () =>
      useObserverStampStore.subscribe((next, prev) => {
        // A restore (session start, twin change) moves `saved` with the stamps: nothing is owed.
        if (next.stamps === prev.stamps || next.saved !== prev.saved) return;
        draftRequestRef.current();
      }),
    [],
  );
  // A Keep mine decision is owed a quiet save the same way (`observer-autogen-review-walkthrough`).
  useEffect(
    () =>
      useSuggestionDecisionStore.subscribe((next, prev) => {
        if (next.decisions === prev.decisions || next.saved !== prev.saved) return;
        draftRequestRef.current();
      }),
    [],
  );

  // A different twin, in place: the last twin's save time or failure says nothing about this one.
  const { reset: resetDraft } = draft;
  useEffect(() => {
    resetDraft();
    // Nor do the last twin's drawn-against records. A twin with a saved plan restores its own on
    // hydrate; one with none starts from nothing (`onboarding-polish-observer-notices`, review).
    useObserverStampStore.getState().restoreStamps({});
    useSuggestionDecisionStore.getState().restore([]);
  }, [twinBid, sessionId, resetDraft]);

  /**
   * Writes a step is still holding back from the plan store — the Observers stage's write-through,
   * which waits for an animation frame (`twin-details-page-versions`, R8.3). A swap can now start from
   * any step, with that step mounted, so each such write is flushed into the store before the store
   * is read. Synchronous: each one writes the store and returns.
   */
  const pendingFlushesRef = useRef(new Set<() => void>());
  const registerPendingWriteFlush = useCallback((flush: () => void) => {
    pendingFlushesRef.current.add(flush);
    return () => {
      pendingFlushesRef.current.delete(flush);
    };
  }, []);
  /**
   * Save unsaved work before a swap, waiting out a save in flight, and resolve whether the server
   * now holds it — so a swap never drops an edit without putting it in the slot, and the stamp it
   * compares is the one the server holds. Shared by Restore, *Load as Draft* and *Discard Draft*.
   */
  const flushForSwap = useCallback(async () => {
    // Every step's held-back write into the store first, so the save below carries it.
    pendingFlushesRef.current.forEach(flush => flush());
    // An observer edit still not in the store would be lost by the swap: refuse it rather than run it.
    if (observerEditPendingRef.current) return false;
    while (savePromiseRef.current) {
      await savePromiseRef.current.catch(() => undefined);
    }
    const { isDirty, layout } = useFloorplanStore.getState();
    // A confirm waiting on its timer is unsaved work too: a swap over it would lose it
    // (`onboarding-polish-observer-notices`).
    if (!isDirty && !referencesUnsaved(layout) && !decisionsUnsaved()) return true;
    return (await persistPlan({ duringSwapFlush: true })).saved;
  }, [persistPlan, observerEditPendingRef]);

  return {
    persistPlan,
    resetDraftMarker,
    saveStampedCodes,
    saveRetiredCodes,
    saveActivatedPlan,
    flushForSwap,
    registerPendingWriteFlush,
    currentLayoutOf,
    stampFirstEdit,
    underlayImages,
    draft,
  };
};

type UseRekeyedPlanSaveArgs = {
  liveConfiguration: { refidsChangedNonce: number; refidsChangedConfigId: number | null };
  persistPlan: (options?: PersistPlanOptions) => Promise<PlanSaveOutcome>;
  currentLayoutOf: (layout: FloorPlannerLayout) => FloorPlannerLayout;
  planIdRef: MutableRefObject<number | null>;
  sessionId: string;
};

export const useRekeyedPlanSave = ({
  liveConfiguration,
  persistPlan,
  currentLayoutOf,
  planIdRef,
  sessionId,
}: UseRekeyedPlanSaveArgs) => {
  /*
   * Save the plan when the re-match after a FILING changed its composite refids (epic ledger row
   * 164). The hook signals only for a re-match against a different configuration than the last.
   *
   * Go Live saves the plan, files — which mints every composite a new id — and re-reads the
   * configuration. The re-match puts the new ids in the store and marks it clean, so without this the
   * saved document keeps the old ones, and the next open meets them as the operator's saved work.
   * `useLiveTwinConfiguration` signals rather than saves: it is a read, and this is the page's one
   * write path, serialised with every other save.
   *
   * ⚠ Only against a plan the server already has. With no id `runPersistPlan` POSTs a create, which
   * is not idempotent, and a plan that was never saved cannot hold a stale id — so there is nothing
   * to correct — checked here as a fast path, and again by `updateOnly` when the save actually runs,
   * since a queued save runs after the one ahead of it and a plan can be cleared meanwhile. A nonce
   * is handled once: `persistPlan` changes identity with its dependencies, and
   * re-running on that alone would save again for the same re-match.
   */
  const handledRefidsNonceRef = useRef(0);
  /**
   * The configuration THIS page's Go Live just filed, set at step 6e and consumed by the first
   * signal that names it. The save is tied to that exact id rather than to "a different
   * configuration": another session activating one, then a Retry here, would otherwise re-match,
   * replace the operator's saved edits with that configuration's copies, and save the loss
   * (Copilot review of #1838).
   */
  const filedConfigIdRef = useRef<number | null>(null);
  useEffect(() => {
    const nonce = liveConfiguration.refidsChangedNonce;
    if (nonce === handledRefidsNonceRef.current) return;
    handledRefidsNonceRef.current = nonce;
    const filedConfigId = filedConfigIdRef.current;
    if (filedConfigId == null || liveConfiguration.refidsChangedConfigId !== filedConfigId) return;
    filedConfigIdRef.current = null;
    if (planIdRef.current == null) return;
    // Re-checked at run time rather than trusted from the re-match: an operator who has started
    // editing since will carry the new ids with their own draft save, and this must not save for them.
    if (useFloorplanStore.getState().isDirty) return;
    // `runPersistPlan` marks the store clean only if the layout it sent is still the store's. An edit
    // made while this save was in flight is not in what it sent, so it must stay dirty.
    const layoutAtStart = useFloorplanStore.getState().layout;
    if (!layoutAtStart) return;
    void persistPlan({
      updateOnly: true,
      unchangedSince: layoutAtStart,
      // Re-minted ids from a re-match are what a load produced, not the person's work.
      edit: false,
    }).then(outcome => {
      const store = useFloorplanStore.getState();
      if (outcome.saved) {
        if (store.layout !== currentLayoutOf(layoutAtStart) && store.twinBID === sessionId) {
          store.setDirty(true);
        }
        return;
      }
      // The plan changed while this save waited behind another: nothing was sent. Not a failure —
      // but the save ahead may have marked the store clean over that change, so mark it dirty.
      if (outcome.reason === SKIPPED_LAYOUT_CHANGED) {
        if (store.twinBID === sessionId) store.setDirty(true);
        return;
      }
      // No dialog: the operator did not ask for this save. Nothing is lost — the store holds the new ids
      // and the reload merge corrects a stale document by identity — so the session is left DIRTY,
      // and the next draft save or the leave guard carries it. Only THIS session's: a save that settles
      // after the page has moved on must not arm another session's leave guard.
      console.error(
        '[live-config] could not save the plan after re-keying composites:',
        planIdRef.current,
        outcome.reason ?? '(no reason)',
      );
      if (store.layout && store.twinBID === sessionId) store.setDirty(true);
    });
  }, [
    liveConfiguration.refidsChangedNonce,
    liveConfiguration.refidsChangedConfigId,
    persistPlan,
    sessionId,
    currentLayoutOf,
    planIdRef,
  ]);

  return filedConfigIdRef;
};
